import { UPLOAD_CHUNK_BYTES, type AttachmentDto, type CreateUploadInput, type UploadSessionDto } from '@dijitalapp/shared';
import { api, ApiError, apiUpload, parseErrorBody } from './api';

/*
 * Attachment uploads. Small files go in one request as before; anything bigger than one chunk is sent in
 * pieces to an upload session, so a dropped connection costs one chunk, not the whole file: the uploader
 * waits for the network (with growing pauses), asks the server where it stands and carries on. It keeps
 * trying while the page is open, until it is cancelled.
 */

export type UploadResult = { attachment: AttachmentDto } | { encrypted: { id: string; size: number } };
export type UploadOptions = Omit<CreateUploadInput, 'size' | 'name'>;
export interface UploadControl {
  onProgress?: (sent: number, total: number) => void;
  signal?: AbortSignal;
}

export class UploadCancelledError extends Error {}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(done, ms);
    // Back online: no need to wait out the pause.
    window.addEventListener('online', done, { once: true });
    signal?.addEventListener('abort', done, { once: true });
    function done() {
      clearTimeout(timer);
      window.removeEventListener('online', done);
      resolve();
    }
  });

/** Network trouble or a server hiccup is worth retrying; a refusal (4xx) is not. */
const retryable = (err: unknown) => err instanceof ApiError && (err.code === 'NETWORK' || err.status >= 500 || err.status === 429);

export async function uploadAttachment(conversationId: string, file: Blob, name: string, opts: UploadOptions, ctl: UploadControl = {}): Promise<UploadResult> {
  const total = file.size;
  if (total <= UPLOAD_CHUNK_BYTES) {
    const form = new FormData();
    if (opts.encrypted) form.append('encrypted', 'true');
    if (opts.voice || opts.round) {
      form.append(opts.round ? 'round' : 'voice', 'true');
      form.append('durationMs', String(Math.round(opts.durationMs ?? 0)));
      if (opts.waveform?.length) form.append('waveform', opts.waveform.join(','));
    }
    if (opts.asDocument) form.append('asDocument', 'true');
    if (opts.sticker) form.append('sticker', 'true');
    form.append('file', file, name);
    const result = await apiUpload<UploadResult>(`/conversations/${conversationId}/attachments`, form);
    ctl.onProgress?.(total, total);
    return result;
  }

  const session = await api<UploadSessionDto>('POST', `/conversations/${conversationId}/uploads`, { size: total, name, ...opts });
  let offset = session.received;
  let failures = 0;
  ctl.onProgress?.(offset, total);
  try {
    while (offset < total) {
      if (ctl.signal?.aborted) throw new UploadCancelledError();
      try {
        offset = await sendChunk(session.id, file, offset, session.chunkBytes, ctl.signal);
        failures = 0;
        ctl.onProgress?.(offset, total);
      } catch (err) {
        if (ctl.signal?.aborted) throw new UploadCancelledError();
        if (err instanceof ApiError && err.code === 'UPLOAD_OFFSET_MISMATCH') {
          offset = Number(err.fields?.['received'] ?? offset);
          continue;
        }
        if (!retryable(err)) throw err;
        failures += 1;
        await sleep(Math.min(30_000, 1000 * 2 ** Math.min(failures - 1, 5)), ctl.signal);
        // Where the server really is (the last chunk may have arrived though its answer did not).
        offset = await api<UploadSessionDto>('GET', `/uploads/${session.id}`).then(
          (s) => s.received,
          () => offset,
        );
      }
    }
    return await completeWithRetry(session.id, ctl.signal);
  } catch (err) {
    if (err instanceof UploadCancelledError) void api('DELETE', `/uploads/${session.id}`).catch(() => undefined);
    throw err;
  }
}

async function sendChunk(id: string, file: Blob, offset: number, chunkBytes: number, signal?: AbortSignal): Promise<number> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/uploads/${id}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/octet-stream', 'upload-offset': String(offset) },
      body: file.slice(offset, offset + chunkBytes),
      ...(signal ? { signal } : {}),
    });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  const json: unknown = await res.json().catch(() => null);
  if (!res.ok) throw parseErrorBody(res.status, json);
  return (json as UploadSessionDto).received;
}

async function completeWithRetry(id: string, signal?: AbortSignal): Promise<UploadResult> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await api<UploadResult>('POST', `/uploads/${id}/complete`);
    } catch (err) {
      if (!retryable(err) || attempt >= 5 || signal?.aborted) throw err;
      await sleep(1000 * attempt, signal);
    }
  }
}
